
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminHttpClient } from '@/admin/lib/admin-http-client';
import { getAdminEndpointUrl } from '@/admin/lib/admin-api-config';

interface PnolDispatchListItem {
  id: string;
  s3Key: string;
  advanceCount: number;
  totalAmount: string;
  triggerSource: 'cron' | 'manual';
  processedAt: string | null;
  createdAt: string;
}

type StatusFilter = 'all' | 'pending' | 'processed';

const formatCLP = (n: number) =>
  n.toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 });

const formatDateTime = (iso: string) => {
  try {
    return new Intl.DateTimeFormat('es-CL', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'America/Santiago' }).format(new Date(iso));
  } catch {
    return iso;
  }
};

interface RunResult {
  processed: number;
  date: string;
}

export default function PnolDispatchesTable() {
  const [rows, setRows] = useState<PnolDispatchListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<StatusFilter>('all');
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState<RunResult | null>(null);
  const [runError, setRunError] = useState<string | null>(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const base = getAdminEndpointUrl('pnolDispatches', 'list');
      const url = `${base}?status=${status}`;
      const data = await adminHttpClient.get<PnolDispatchListItem[]>(url);
      setRows(data);
    } catch (err) {
      console.error('Error cargando lotes PNOL', err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  const handleRunNow = useCallback(async () => {
    if (running) return;
    setRunning(true);
    setRunResult(null);
    setRunError(null);
    try {
      const data = await adminHttpClient.post<RunResult>(getAdminEndpointUrl('pnolDispatches', 'run'), {});
      setRunResult(data);
      await fetchList();
    } catch (err) {
      setRunError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setRunning(false);
    }
  }, [fetchList, running]);

  const pendingCount = rows.filter((r) => r.processedAt === null).length;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border bg-card p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2">
            {(['all', 'pending', 'processed'] as const).map((option) => (
              <button
                key={option}
                onClick={() => setStatus(option)}
                className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                  status === option
                    ? 'bg-blue-600 text-white'
                    : 'border border-input bg-background text-muted-foreground hover:bg-accent'
                }`}
              >
                {option === 'all' ? 'Todos' : option === 'pending' ? 'Pendientes' : 'Procesados'}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground">
              {rows.length} lote(s){status === 'all' && pendingCount > 0 ? ` · ${pendingCount} pendiente(s) de envío` : ''}
            </span>
            <button
              onClick={() => void handleRunNow()}
              disabled={running}
              title="Equivalente a `pnpm trigger:disbursements` — arma un lote PNOL ahora con los adelantos APPROVED."
              className="inline-flex items-center gap-1.5 rounded-md bg-purple-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition-colors hover:bg-purple-500 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {running ? (
                <>
                  <span className="material-symbols-outlined animate-spin text-[14px]">progress_activity</span>
                  Disparando…
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined text-[14px]">pan_tool</span>
                  Disparar desembolso ahora
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {runResult && (
        <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-800 dark:bg-emerald-900/20">
          <span className="material-symbols-outlined mt-0.5 text-[20px] text-emerald-600">check_circle</span>
          <div className="flex-1 space-y-1 text-sm text-emerald-900 dark:text-emerald-200">
            <p className="font-semibold">
              {runResult.processed > 0
                ? `Corrida manual completada · ${runResult.processed} adelanto(s) desembolsado(s).`
                : 'Corrida manual completada · sin adelantos APPROVED elegibles, no se generó lote.'}
            </p>
            <p className="text-xs text-emerald-700 dark:text-emerald-300">{formatDateTime(runResult.date)}</p>
          </div>
          <button onClick={() => setRunResult(null)} className="text-emerald-700 hover:text-emerald-900 dark:text-emerald-300">
            <span className="material-symbols-outlined text-[18px]">close</span>
          </button>
        </div>
      )}

      {runError && (
        <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
          <span className="material-symbols-outlined mt-0.5 text-[20px] text-red-600">error</span>
          <div className="flex-1 text-sm text-red-900 dark:text-red-200">
            <p className="font-semibold">No se pudo disparar la corrida.</p>
            <p className="mt-1 break-all font-mono text-xs">{runError}</p>
          </div>
          <button onClick={() => setRunError(null)} className="text-red-700 hover:text-red-900 dark:text-red-300">
            <span className="material-symbols-outlined text-[18px]">close</span>
          </button>
        </div>
      )}

      <div className="rounded-xl border bg-card overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="text-left text-xs uppercase text-muted-foreground">
            <tr className="border-b border-border">
              <th className="py-3 px-4">Generado</th>
              <th className="py-3 px-4">Origen</th>
              <th className="py-3 px-4 text-right">Adelantos</th>
              <th className="py-3 px-4 text-right">Monto total</th>
              <th className="py-3 px-4">Estado</th>
              <th className="py-3 px-4"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">Cargando…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">No hay lotes PNOL con los filtros actuales.</td></tr>
            )}
            {!loading && rows.map((row) => (
              <tr key={row.id} className="border-t border-border hover:bg-accent/40">
                <td className="py-3 px-4">
                  <div className="font-medium text-foreground">{formatDateTime(row.createdAt)}</div>
                  <div className="text-xs text-muted-foreground font-mono">{row.id}</div>
                </td>
                <td className="py-3 px-4 text-xs">
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${
                    row.triggerSource === 'cron'
                      ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                      : 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                  }`}>
                    <span className="material-symbols-outlined text-[12px]">{row.triggerSource === 'cron' ? 'schedule' : 'pan_tool'}</span>
                    {row.triggerSource === 'cron' ? 'Cron' : 'Manual'}
                  </span>
                </td>
                <td className="py-3 px-4 text-right font-medium tabular-nums">{row.advanceCount}</td>
                <td className="py-3 px-4 text-right font-medium tabular-nums">{formatCLP(Number(row.totalAmount))}</td>
                <td className="py-3 px-4 text-xs">
                  {row.processedAt ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                      <span className="material-symbols-outlined text-[12px]">check_circle</span>
                      Enviado · {formatDateTime(row.processedAt)}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                      <span className="material-symbols-outlined text-[12px]">pending</span>
                      Pendiente de envío
                    </span>
                  )}
                </td>
                <td className="py-3 px-4 text-right">
                  <Link
                    to={`/admin/pnol-dispatches/${row.id}`}
                    className="inline-flex items-center gap-1 rounded-md border border-input bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent"
                  >
                    Ver detalle
                    <span className="material-symbols-outlined text-[14px]">chevron_right</span>
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
